import {
  useBrandAppConfig,
  useConciergeContext,
  getAppConsistentParams,
  getProductUrl,
} from "@brandclub/common-ui";
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import "./concierge-tailwind.css";
import { ConciergeChatAreaV2 } from "./components/ConciergeChatAreaV2";
import { ConciergeSidebar } from "./components/ConciergeSidebar";
import { useAutoScroll } from "./hooks/useAutoScroll";
import { useAutoSendFromUrl } from "./hooks/useAutoSendFromUrl";
import { useConversationLoader } from "./hooks/useConversationLoader";
import { useStreamingSearch } from "./hooks/useStreamingSearch";
import { useUrlSync } from "./hooks/useUrlSync";
import BagDrawer from "@/components/Checkout/BagDrawer";
import { useCheckoutDrawer } from "@/components/Checkout/useCheckoutDrawer";
import { Drawer } from "@mui/material";

const ConciergeContainer = () => {
  // Navigation
  const navigate = useNavigate();

  // Brand config
  const { data: config } = useBrandAppConfig();
  const params = new URLSearchParams(window.location.search);
  const brandId = params.get("brandId") || null;
  const appConfigId = params.get("appConfigId") || null;

  // Checkout drawer state
  const { isOpen: isCheckoutDrawerOpen } = useCheckoutDrawer();

  // Input state
  const [searchInputValue, setSearchInputValue] = useState("");

  // Conversation state (centralized via context)
  const conversationState = useConciergeContext();

  // Auto-scroll behavior
  const {
    chatWrapperRef,
    messagesEndRef,
    shouldScrollOnNextMessageRef,
    scrollToBottom,
  } = useAutoScroll(
    conversationState.conversationHistory.length,
    conversationState.isActivelyStreaming,
    conversationState.streamingAIResponse,
    conversationState.conversationHistory.length === 0
  );

  // Streaming search
  const { performSearch } = useStreamingSearch({
    brandId,
    appConfigId,
    conversationId: conversationState.conversationId,
    setConversationId: conversationState.setConversationId,
    setIsActivelyStreaming: conversationState.setIsActivelyStreaming,
    setStreamingAIResponse: conversationState.setStreamingAIResponse,
    setCurrentStatus: conversationState.setCurrentStatus,
    setConversationHistory: conversationState.setConversationHistory,
    setIsWaitingForFollowUpQueries:
      conversationState.setIsWaitingForFollowUpQueries,
    setStreamingProductGroups: conversationState.setStreamingProductGroups,
    setProductMetadataMap: conversationState.setProductMetadataMap,
    setPreviewSearchQueries: conversationState.setPreviewSearchQueries,
    setPreviewQueryProducts: conversationState.setPreviewQueryProducts,
    setFollowUpQuestions: conversationState.setFollowUpQuestions,
    setStreamingFeaturedProduct: conversationState.setStreamingFeaturedProduct,
    setStreamingStoreLocator: conversationState.setStreamingStoreLocator,
    scrollToBottom,
    shouldScrollOnNextMessageRef,
  });

  // Handle search submission
  const handleSearch = async (
    searchTerm: string,
    imageData?: string,
    imageMimeType?: string
  ) => {
    setSearchInputValue("");
    await performSearch(searchTerm, imageData, imageMimeType);
  };

  // Handle update feedback
  const handleUpdateFeedback = (
    messageIndex: number,
    feedback: { type: "like" | "dislike"; timestamp: string } | null
  ) => {
    // Update the feedback in the conversation history
    conversationState.setConversationHistory((prev) => {
      return prev.map((msg, idx) => {
        if (idx === messageIndex) {
          return {
            ...msg,
            feedback: feedback || undefined,
          };
        }
        return msg;
      });
    });
  };

  // Handle variant selection - navigate to the variant's PDP
  const handleVariantSelect = useCallback(
    (stacklineSku: string) => {
      // Find the variant in the product metadata map to get its title
      let variantTitle = "";
      conversationState.productMetadataMap.forEach((product) => {
        const variant = product.variants?.find(
          (v) => v.stacklineSku === stacklineSku
        );
        if (variant) {
          variantTitle = variant.title;
        }
      });

      // Build the URL using existing routing logic
      const urlSearchParams = getAppConsistentParams(window.location.search);
      const targetUrl = getProductUrl({
        title: variantTitle,
        stacklineSku,
        urlSearchParams,
      });

      navigate({
        pathname: targetUrl.split("?")[0],
        search: targetUrl.split("?")[1],
      });
    },
    [conversationState.productMetadataMap, navigate]
  );

  // Handle conversation selection from history
  const handleConversationSelect = (conversationId: string) => {
    // If we're already viewing this conversation, just reload it by updating the ID
    // This ensures a fresh load without the jarring clear + reload
    if (conversationState.conversationId === conversationId) {
      // Force reload by temporarily clearing and resetting the ID
      // This will trigger useConversationLoader to reload
      conversationState.setConversationId(null);
      // Use setTimeout to ensure the state update is processed
      setTimeout(() => {
        conversationState.setConversationId(conversationId);
      }, 0);
      return;
    }

    // Clear current conversation state before loading new one
    conversationState.setConversationHistory([]);
    conversationState.setProductMetadataMap(new Map());
    conversationState.setStreamingProductGroups(new Map());
    conversationState.setPreviewSearchQueries([]);
    conversationState.setPreviewQueryProducts([]);
    conversationState.setIsWaitingForFollowUpQueries(false);
    conversationState.setStreamingAIResponse("");
    conversationState.setCurrentStatus("");
    conversationState.setFollowUpQuestions([]);

    // Update URL with the selected conversation ID
    const params = new URLSearchParams(window.location.search);
    params.set("conversationId", conversationId);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);

    // Update conversation state
    conversationState.setConversationId(conversationId);
  };

  // Handle compose button click (go back to welcome screen)
  const handleComposeClick = () => {
    // Clear conversation state
    conversationState.setConversationHistory([]);
    conversationState.setProductMetadataMap(new Map());
    conversationState.setStreamingProductGroups(new Map());
    conversationState.setPreviewSearchQueries([]);
    conversationState.setPreviewQueryProducts([]);
    conversationState.setIsWaitingForFollowUpQueries(false);
    conversationState.setStreamingAIResponse("");
    conversationState.setCurrentStatus("");
    conversationState.setFollowUpQuestions([]);
    conversationState.setConversationId(null);

    // Remove conversationId from URL
    const params = new URLSearchParams(window.location.search);
    params.delete("conversationId");
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.pushState({}, "", newUrl);
  };

  // Conversation loader
  const { isLoadingConversationQuery } = useConversationLoader({
    conversationId: conversationState.conversationId,
    isActivelyStreaming: conversationState.isActivelyStreaming,
    conversationHistoryLength: conversationState.conversationHistory.length,
    onConversationLoaded: (
      messages,
      productMap,
      followUpQuestions,
      previewSearchQueries,
      previewQueryProducts
    ) => {
      conversationState.setProductMetadataMap(productMap);
      conversationState.setConversationHistory(messages);
      conversationState.setFollowUpQuestions(followUpQuestions);
      conversationState.setPreviewSearchQueries(previewSearchQueries);
      conversationState.setPreviewQueryProducts(previewQueryProducts);
    },
    onConversationNotFound: () => {
      const params = new URLSearchParams(window.location.search);
      params.delete("conversationId");
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
      conversationState.setConversationId(null);
    },
  });

  // Sync conversationId with URL
  useUrlSync(conversationState.conversationId);

  // Auto-send from URL params
  useAutoSendFromUrl({
    isActivelyStreaming: conversationState.isActivelyStreaming,
    conversationLength: conversationState.conversationHistory.length,
    onSearch: handleSearch,
  });

  const showWelcome =
    conversationState.conversationHistory.length === 0 &&
    !isLoadingConversationQuery;

  return (
    <>
      <Toaster />

      <div
        className="concierge-root"
        style={{
          display: "flex",
          flexDirection: "row",
          height: "100dvh",
          minHeight: "-webkit-fill-available",
        }}
      >
        {/* Sidebar */}
        <ConciergeSidebar
          onConversationSelect={handleConversationSelect}
          onComposeClick={handleComposeClick}
          currentConversationId={conversationState.conversationId}
        />

        {/* Main content area */}
        <div
          className="tw:relative tw:flex tw:flex-1 tw:flex-col"
          style={{ minWidth: 0 }}
        >
          <ConciergeChatAreaV2
            appName={config?.appName}
            brandId={brandId}
            conversationId={conversationState.conversationId}
            conversationHistory={conversationState.conversationHistory}
            productMetadataMap={conversationState.productMetadataMap}
            streamingProductGroups={conversationState.streamingProductGroups}
            previewSearchQueries={conversationState.previewSearchQueries}
            previewQueryProducts={conversationState.previewQueryProducts}
            followUpQuestions={conversationState.followUpQuestions}
            streamingFeaturedProduct={
              conversationState.streamingFeaturedProduct
            }
            streamingStoreLocator={conversationState.streamingStoreLocator}
            isActivelyStreaming={conversationState.isActivelyStreaming}
            streamingAIResponse={conversationState.streamingAIResponse}
            currentStatus={conversationState.currentStatus}
            isWaitingForFollowUpQueries={
              conversationState.isWaitingForFollowUpQueries
            }
            isLoadingConversationQuery={isLoadingConversationQuery}
            handleSearch={handleSearch}
            onUpdateFeedback={handleUpdateFeedback}
            onVariantSelect={handleVariantSelect}
            searchInputValue={searchInputValue}
            setSearchInputValue={setSearchInputValue}
          />
        </div>
      </div>
      <Drawer
        id="cart-popover"
        open={isCheckoutDrawerOpen}
        sx={{
          zIndex: 1300,
          width: 462,
          maxWidth: "100%",
        }}
        PaperProps={{
          sx: {
            boxShadow: "none",
            borderRadius: "0",
            maxWidth: "100%",
          },
        }}
        anchor="right"
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
        <BagDrawer />
      </Drawer>
    </>
  );
};

export default ConciergeContainer;
